import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DMAutomationPlatform,
  InstagramAccount,
  IntegrationConnectionStatus,
  NotificationType,
} from "@markit/common.types";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../../Containers/PopupModalContainer";
import ConfirmDeleteModal from "../../../Containers/ConfirmPopups/ConfirmDeleteModal";
import ConfirmActionModal from "../../../Containers/ConfirmPopups/ConfirmActionModal";
import RectangleButton from "../../../Buttons/RectangleButton";
import { Colors } from "../../../../utils/colors";
import {
  accountActions,
  getAccountState,
  loadInitialInstagramAccounts,
} from "../../../../redux/slices/accountSlice";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import { updateDoc } from "../../../../firebase";
import { getUserInstagramAccountRef } from "../../../../utils/FirebaseUtils";
import {
  disconnectInstagramAccount,
  refreshInstagramAccount,
} from "../../../../utils/instagramApiUtils";
import { getAccountConnectionStatusDisplay } from "../../../../utils/socialAccountUtils";
import { GetNumericDate } from "../../../../utils/GetTime";
import SocialProfilePic from "../../../DisplayItem/AutoDM/SocialProfilePic";
import { SocialPlatformIconItem } from "../../../DisplayItem/AutoDM/SocialPlatformIconItem";
import ConnectInstagramModal from "./ConnectInstagramModal";

type ManageInstagramAccountModalProps = {
  instagramAccount: InstagramAccount;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
};

// Manage actions for a single connected Instagram account (Connections tab row).
const ManageInstagramAccountModal = (
  props: ManageInstagramAccountModalProps
) => {
  const { instagramAccount, showModal, setShowModal } = props;
  const { accountData } = useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showConfirmToggleModal, setShowConfirmToggleModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showReconnectModal, setShowReconnectModal] = useState(false);

  const isPaused = useMemo(
    () => instagramAccount.status === IntegrationConnectionStatus.PAUSED,
    [instagramAccount.status]
  );

  const isErrored = useMemo(
    () => instagramAccount.status === IntegrationConnectionStatus.ERROR,
    [instagramAccount.status]
  );

  const statusDisplay = useMemo(
    () => getAccountConnectionStatusDisplay(instagramAccount.status),
    [instagramAccount.status]
  );

  const disconnectOnPress = useCallback(async () => {
    setIsDisconnecting(true);
    await disconnectInstagramAccount(instagramAccount.id);
    await dispatch(loadInitialInstagramAccounts(accountData.uid));
    setIsDisconnecting(false);
    showNotificationBanner(
      dispatch,
      "Disconnected Account",
      NotificationType.NEGATIVE
    );
    setShowModal(false);
  }, [accountData.uid, dispatch, instagramAccount.id, setShowModal]);

  const toggleConnectionStatus = useCallback(async () => {
    const newStatus = isPaused
      ? IntegrationConnectionStatus.CONNECTED
      : IntegrationConnectionStatus.PAUSED;
    const updatedInstagramAccount = { ...instagramAccount, status: newStatus };
    setIsToggling(true);
    const instagramAccountRef = getUserInstagramAccountRef(
      accountData.uid,
      instagramAccount.id
    );
    await updateDoc(instagramAccountRef, updatedInstagramAccount);
    await dispatch(
      accountActions.updateInstagramAccount(updatedInstagramAccount)
    );
    setIsToggling(false);
    showNotificationBanner(
      dispatch,
      isPaused ? "Reactivated Account" : "Paused Account",
      NotificationType.AFFIRMATIVE
    );
  }, [accountData.uid, dispatch, instagramAccount, isPaused]);

  const refreshOnPress = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const response = await refreshInstagramAccount(instagramAccount.id);
      const updatedInstagramAccount = { ...instagramAccount, ...response };
      await dispatch(
        accountActions.updateInstagramAccount(updatedInstagramAccount)
      );
      showNotificationBanner(
        dispatch,
        "Refreshed Account",
        NotificationType.AFFIRMATIVE
      );
    } catch (err) {
      console.error(err);
      // The backend may have just flipped this account's status to Error -
      // re-pull so the modal reflects that instead of continuing to show
      // "Connected" behind the failure banner.
      await dispatch(loadInitialInstagramAccounts(accountData.uid));
      showNotificationBanner(
        dispatch,
        "Failed to refresh this account",
        NotificationType.NEGATIVE
      );
    } finally {
      setIsRefreshing(false);
    }
  }, [accountData.uid, dispatch, instagramAccount]);

  const actionRows = [
    isErrored
      ? {
          title: "Reconnect Account",
          description:
            "Your Instagram session has expired or was invalidated. Reconnect to keep automations running.",
          buttonLabel: "Reconnect",
          destructive: false,
          loading: false,
          onPress: () => setShowReconnectModal(true),
        }
      : {
          title: "Refresh Account",
          description: "Updates your account details if any is out-of-sync.",
          buttonLabel: "Refresh",
          destructive: false,
          loading: isRefreshing,
          onPress: refreshOnPress,
        },
    ...(isErrored
      ? []
      : [
          {
            title: `${isPaused ? "Reactivate" : "Pause"} All Automations`,
            description:
              "Temporarily pause automations to stop them from being triggered. Can reactivate them at any time.",
            buttonLabel: isPaused ? "Reactivate" : "Pause",
            destructive: false,
            loading: false,
            onPress: () => setShowConfirmToggleModal(true),
          },
        ]),
    {
      title: "Disconnect Account",
      description:
        "Remove Markit's connection to your account. For full removal on IG's side, revoke Markit in your IG account settings.",
      buttonLabel: "Disconnect",
      destructive: true,
      loading: false,
      onPress: () => setShowDisconnectModal(true),
    },
  ];

  return (
    <>
      <PopupModalContainer
        hideModal={!showModal}
        closeModal={() => setShowModal(false)}
        modalContentWidth={PopupModalContainerWidth.SMALL}
        closeOnOutsidePress={
          !showDisconnectModal && !showConfirmToggleModal && !showReconnectModal
        }
        noExit
        headerComp={
          <div className="ColumnNormal" style={{ gap: 14, fontWeight: 400 }}>
            <span className="sectionTitle">Manage Account</span>
            <div className="AlignedRow" style={{ gap: 14 }}>
              <SocialProfilePic account={instagramAccount} size={40} />
              <div className="ColumnNormal" style={{ gap: 4 }}>
                <div className="AlignedRow" style={{ gap: 4 }}>
                  <SocialPlatformIconItem
                    platform={DMAutomationPlatform.INSTAGRAM}
                    size={16}
                    noBorder
                  />
                  <span className="bodyRegular">
                    @{instagramAccount.username}
                  </span>
                </div>
                <span className="smallBodySubtext">
                  Connected On: {GetNumericDate(instagramAccount.connectedOn)}
                </span>
                <div className="AlignedRow" style={{ gap: 4 }}>
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: statusDisplay.color,
                    }}
                  />
                  <span
                    className="smallBodySubtext"
                    style={{ color: statusDisplay.color }}
                  >
                    {statusDisplay.label}
                  </span>
                </div>
              </div>
            </div>
          </div>
        }
        valueComp={
          <div className="ColumnNormal" style={{ gap: 24 }}>
            {actionRows.map((row) => (
              <div
                key={row.title}
                className="AlignedRowSpaced"
                style={{ gap: 40 }}
              >
                <div
                  className="ColumnNormal"
                  style={{ gap: 4, flex: 1, minWidth: 0 }}
                >
                  <span className="bodyMedium">{row.title}</span>
                  <span className="smallBodySubtext">{row.description}</span>
                </div>
                <RectangleButton
                  buttonLabel={row.buttonLabel}
                  onPress={row.onPress}
                  loading={row.loading}
                  altColor={row.destructive ? Colors.RED3 : Colors.BLACK}
                  altTextColor={Colors.WHITE}
                  altPaddingHorz={24}
                  altPaddingVert={14}
                  width={126}
                />
              </div>
            ))}
          </div>
        }
      />
      {showDisconnectModal ? (
        <ConfirmDeleteModal
          heading={`Disconnect @${instagramAccount.username}`}
          subtext="Are you sure you want to disconnect this Instagram account? All automations for this account will be stopped, but can be restored if you decide to reconnect this account later."
          deleteButtonText="Disconnect"
          hideModal={!showDisconnectModal}
          setIsVisible={setShowDisconnectModal}
          deleteOnPress={disconnectOnPress}
          buttonLoading={isDisconnecting}
        />
      ) : null}
      {showConfirmToggleModal ? (
        <ConfirmActionModal
          heading={
            isPaused
              ? `Reactivate automations on @${instagramAccount.username}?`
              : `Pause automations on @${instagramAccount.username}?`
          }
          subtext={
            isPaused
              ? "All active automations on this account will begin triggering again."
              : "All DMs that may be currently processing on this account will not be sent anymore. You can reactivate at any time."
          }
          confirmButtonText={isPaused ? "Reactivate" : "Pause"}
          hideModal={!showConfirmToggleModal}
          setIsVisible={() => setShowConfirmToggleModal(false)}
          confirmOnPress={toggleConnectionStatus}
          loadingConfirm={isToggling}
        />
      ) : null}
      <ConnectInstagramModal
        showModal={showReconnectModal}
        setShowModal={setShowReconnectModal}
      />
    </>
  );
};

export default ManageInstagramAccountModal;
